import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Homeowners.css';

const Homeowners = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const playerRef = useRef(null);

    useEffect(() => {
        setIsLoaded(true);

        // Load YouTube IFrame API
        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }

        // Initialize player function
        const initPlayer = () => {
            playerRef.current = new window.YT.Player('youtube-player', {
                videoId: '44xCwod0F-w',
                playerVars: {
                    autoplay: 1,
                    controls: 0,
                    disablekb: 1,
                    loop: 1,
                    playlist: '44xCwod0F-w',
                    modestbranding: 1,
                    rel: 0,
                    showinfo: 0,
                    mute: 1,
                    playsinline: 1
                },
                events: {
                    onReady: (event) => {
                        event.target.playVideo();
                        setIsMuted(true);
                    }
                }
            });
        };

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            window.onYouTubeIframeAPIReady = initPlayer;
        }

        return () => {
            // Cleanup not strictly necessary for simple iframe but good practice
        };

    }, []);

    const toggleMute = () => {
        if (playerRef.current && playerRef.current.unMute) {
            if (isMuted) {
                playerRef.current.unMute();
                setIsMuted(false);
            } else {
                playerRef.current.mute();
                setIsMuted(true);
            }
        }
    };

    return (
        <div className={`page homeowners-page ${isLoaded ? 'loaded' : ''}`}>
            {/* Split Hero Section */}
            <section className="homeowners-split-hero">
                <div className="container">
                    <div className="split-layout">
                        {/* Left Column: Video */}
                        <div className="video-column">
                            <div className="video-wrapper">
                                <div id="youtube-player" className="homeowners-video"></div>
                                <button
                                    className={`video-control-btn ${isMuted ? 'muted' : 'unmuted'}`}
                                    onClick={toggleMute}
                                    aria-label={isMuted ? "Unmute video" : "Mute video"}
                                >
                                    {isMuted ? (
                                        <>
                                            <span>Sound On</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
                                        </>
                                    ) : (
                                        <>
                                            <span>Sound Off</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Right Column: Content */}
                        <div className="content-column">
                            <h1>your home already knows.</h1>
                        </div>
                    </div>

                    {/* Centered Tagline */}
                    <div className="centered-tagline-wrapper">
                        <p className="main-tagline">Technology that disappears into architecture.<span className="tm">™</span></p>
                    </div>
                </div>
            </section>

            {/* The Point */}
            <section className="point-section">
                <div className="container">
                    <h2>The best technology is the kind you forget is there.</h2>
                    <p>
                        Your home just works—responding to you, your family, and the
                        way you actually live.
                    </p>
                </div>
            </section>

            {/* Concerns */}
            <section className="concerns-section">
                <div className="container">
                    <span className="label">Honest answers to honest questions</span>

                    <div className="concerns-grid">
                        <div className="concern">
                            <h3>"Is this going to be complicated to use?"</h3>
                            <p>
                                No. If it requires a manual, we've failed. We design for the
                                person in your household who doesn't care about technology—if
                                they can use it intuitively, we've succeeded.
                            </p>
                        </div>
                        <div className="concern">
                            <h3>"What happens when something breaks?"</h3>
                            <p>
                                We're available. And because we document everything, any qualified
                                service provider can work on your system—you're never locked in
                                to one company.
                            </p>
                        </div>
                        <div className="concern">
                            <h3>"Will this feel cold or robotic?"</h3>
                            <p>
                                The opposite. Great automation feels warm and human. It's the
                                fireplace that's already going when you settle into the living
                                room. It's atmosphere, not technology.
                            </p>
                        </div>
                        <div className="concern">
                            <h3>"How much does this cost?"</h3>
                            <p>
                                It depends on the scope, but we're transparent. We provide
                                itemized budgets for the agreed scope. Design, equipment,
                                installation and ongoing support are explained before you commit.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How this works */}
            <section className="how-section">
                <div className="container">
                    <span className="label">How this typically works</span>
                    <h2>You won't deal with the complexity. We will.</h2>
                    <p className="how-intro">
                        Usually, your architect or builder brings us in. But if you're an
                        owner exploring automation for a new build or renovation, here's
                        what to expect:
                    </p>

                    <div className="how-steps">
                        <div className="how-step">
                            <span className="step-num">1</span>
                            <div>
                                <h4>Conversation</h4>
                                <p>We learn about your lifestyle, not your network requirements.</p>
                            </div>
                        </div>
                        <div className="how-step">
                            <span className="step-num">2</span>
                            <div>
                                <h4>Design</h4>
                                <p>We work with your architect and builder to plan the system invisibly.</p>
                            </div>
                        </div>
                        <div className="how-step">
                            <span className="step-num">3</span>
                            <div>
                                <h4>Installation</h4>
                                <p>Our partner technicians execute during construction—minimal disruption.</p>
                            </div>
                        </div>
                        <div className="how-step">
                            <span className="step-num">4</span>
                            <div>
                                <h4>Programming</h4>
                                <p>Every scene, every automation, tuned to how you live.</p>
                            </div>
                        </div>
                        <div className="how-step">
                            <span className="step-num">5</span>
                            <div>
                                <h4>Handoff</h4>
                                <p>We walk you through everything. And we're always a call away.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="homeowners-cta">
                <div className="container">
                    <h2>Curious what's possible for your home?</h2>
                    <p>
                        Whether you're building new or renovating, we'd enjoy explaining
                        what great automation feels like.
                    </p>
                    <Link to="/contact" className="btn btn-primary">Start a conversation</Link>
                </div>
            </section>
        </div>
    );
};

export default Homeowners;
